import { useState, useMemo } from 'react';
import {
  Alert,
  Button,
  Input,
  Select,
  Space,
  Table,
  Tag,
} from 'antd';
import {
  EditOutlined,
  SearchOutlined,
  UserAddOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import type { ColumnsType } from 'antd/es/table';
import { trpc } from '../../../lib/api';
import { CompanyUserModal } from './CompanyUserModal';

interface CompanyUsersTabProps {
  companyId: string;
  companyName: string;
}

interface User {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'manager' | 'user' | 'viewer';
  department?: string | null;
  position?: string | null;
  isActive: boolean;
  lastLoginAt?: Date | string | null;
  createdAt: Date | string;
}

const roleLabels: Record<string, string> = {
  admin: '管理者',
  manager: 'マネージャー',
  user: 'ユーザー',
  viewer: '閲覧者',
};

const roleColors: Record<string, string> = {
  admin: 'red',
  manager: 'orange',
  user: 'blue',
  viewer: 'default',
};

const formatDateTime = (value?: string | Date | null) => {
  if (!value) return '-';
  return dayjs(value).format('YYYY/MM/DD HH:mm');
};

export const CompanyUsersTab = ({ companyId, companyName }: CompanyUsersTabProps) => {
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<'admin' | 'manager' | 'user' | 'viewer' | undefined>(undefined);
  const [statusFilter, setStatusFilter] = useState<'active' | 'inactive' | undefined>(undefined);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');

  const {
    data: usersData,
    isLoading,
    refetch,
  } = trpc.admin.getUsers.useQuery(
    {
      companyId,
      search: search || undefined,
      role: roleFilter,
      status: statusFilter,
      page,
      limit: pageSize,
      sortBy: 'created_at',
      sortOrder: 'desc',
    },
    {
      enabled: !!companyId,
    }
  );

  const handleCreate = () => {
    setEditingUser(null);
    setModalMode('create');
    setIsModalOpen(true);
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    setModalMode('edit');
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingUser(null);
  };

  const handleModalSuccess = () => {
    setIsModalOpen(false);
    setEditingUser(null);
    refetch();
  };

  const columns: ColumnsType<User> = useMemo(
    () => [
      {
        title: '名前',
        dataIndex: 'name',
        key: 'name',
        render: (text: string, record) => (
          <div>
            <div style={{ fontWeight: 500 }}>{text}</div>
            <div style={{ fontSize: 12, color: '#999' }}>{record.email}</div>
          </div>
        ),
      },
      {
        title: 'ロール',
        dataIndex: 'role',
        key: 'role',
        width: 120,
        render: (role: string) => (
          <Tag color={roleColors[role] || 'default'}>
            {roleLabels[role] || role}
          </Tag>
        ),
      },
      {
        title: '部署',
        dataIndex: 'department',
        key: 'department',
        width: 150,
        render: (value: string | null) => value || '-',
      },
      {
        title: '役職',
        dataIndex: 'position',
        key: 'position',
        width: 150,
        render: (value: string | null) => value || '-',
      },
      {
        title: '状態',
        dataIndex: 'isActive',
        key: 'isActive',
        width: 80,
        render: (value: boolean) => (
          <Tag color={value ? 'success' : 'error'}>
            {value ? '有効' : '無効'}
          </Tag>
        ),
      },
      {
        title: '最終ログイン',
        dataIndex: 'lastLoginAt',
        key: 'lastLoginAt',
        width: 160,
        render: (value: string | Date | null) => formatDateTime(value),
      },
      {
        title: '登録日',
        dataIndex: 'createdAt',
        key: 'createdAt',
        width: 160,
        render: (value: string | Date) => formatDateTime(value),
      },
      {
        title: '操作',
        key: 'actions',
        width: 80,
        render: (_, record) => (
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
        ),
      },
    ],
    []
  );

  return (
    <div>
      <Alert
        type="info"
        message={`「${companyName}」に所属するユーザーを管理できます。`}
        style={{ marginBottom: 16 }}
      />

      <Space
        style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}
        wrap
      >
        <Space wrap>
          <Input
            placeholder="名前・メールで検索"
            prefix={<SearchOutlined />}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            style={{ width: 200 }}
            allowClear
          />
          <Select
            placeholder="ロール"
            value={roleFilter}
            onChange={(value) => {
              setRoleFilter(value);
              setPage(1);
            }}
            style={{ width: 140 }}
            allowClear
            options={[
              { value: 'admin', label: '管理者' },
              { value: 'manager', label: 'マネージャー' },
              { value: 'user', label: 'ユーザー' },
              { value: 'viewer', label: '閲覧者' },
            ]}
          />
          <Select
            placeholder="状態"
            value={statusFilter}
            onChange={(value) => {
              setStatusFilter(value);
              setPage(1);
            }}
            style={{ width: 100 }}
            allowClear
            options={[
              { value: 'active', label: '有効' },
              { value: 'inactive', label: '無効' },
            ]}
          />
        </Space>
        <Button type="primary" icon={<UserAddOutlined />} onClick={handleCreate}>
          ユーザーを追加
        </Button>
      </Space>

      <Table
        columns={columns}
        dataSource={(usersData?.users ?? []) as User[]}
        loading={isLoading}
        rowKey="id"
        pagination={{
          current: page,
          pageSize,
          total: usersData?.pagination?.total ?? 0,
          showSizeChanger: true,
          showTotal: (total) => `全 ${total} 件`,
          onChange: (p, ps) => {
            setPage(p);
            setPageSize(ps);
          },
        }}
        scroll={{ x: 900 }}
      />

      <CompanyUserModal
        open={isModalOpen}
        mode={modalMode}
        companyId={companyId}
        companyName={companyName}
        user={editingUser}
        onClose={handleModalClose}
        onSuccess={handleModalSuccess}
      />
    </div>
  );
};
